import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import ImplantPopup from '../components/ImplantPopup';

const Implants = () => {
  const [implants, setImplants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImplant, setSelectedImplant] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchImplants();
  }, [searchTerm]);

  const fetchImplants = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/implants', { params });
      setImplants(response.data.implants || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch implants');
    } finally {
      setLoading(false);
    }
  };

  const handleImplantClick = (implantName) => {
    setSelectedImplant(implantName);
  };

  const handleClosePopup = () => {
    setSelectedImplant(null);
    fetchImplants();
  };

  const totalPages = Math.ceil(implants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedImplants = implants.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Box sx={{ padding: 3 }}>
      <Typography level="h1" sx={{ mb: 3, color: '#eb7a24' }}>
        Implants
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, p: 2 }}>
        <FormControl sx={{ maxWidth: 400 }}>
          <FormLabel>Search by Implant Name</FormLabel>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search implants..."
            sx={{
              '&:hover': { borderColor: '#ea7a23' },
              '&:focus-within': { borderColor: '#ea7a23' }
            }}
          />
        </FormControl>
      </Card>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="body-md">
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, implants.length)} of {implants.length} implants
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                sx={{
                  borderColor: '#eb7a24',
                  color: '#eb7a24',
                  '&:hover': { borderColor: '#cc6a1f', backgroundColor: 'rgba(235, 122, 36, 0.1)' }
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
                sx={{
                  borderColor: '#eb7a24',
                  color: '#eb7a24',
                  '&:hover': { borderColor: '#cc6a1f', backgroundColor: 'rgba(235, 122, 36, 0.1)' }
                }}
              >
                Next
              </Button>
            </Box>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: '#eb7a24',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: '#fff4e6',
                  cursor: 'pointer'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Implant Name</th>
                  <th style={{ width: '14%' }}>Size(s)</th>
                  <th style={{ width: '16%' }}>Vendor</th>
                  <th style={{ width: '12%' }}>Total Quantity</th>
                  <th style={{ width: '18%' }}>Created By</th>
                </tr>
              </thead>
              <tbody>
                {paginatedImplants.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                      No implants found
                    </td>
                  </tr>
                ) : (
                  paginatedImplants.map((implant) => (
                    <tr
                      key={implant.implant_name}
                      onClick={() => handleImplantClick(implant.implant_name)}
                    >
                      <td style={{ color: '#eb7a24', fontWeight: 'bold' }}>
                        {implant.implant_name}
                      </td>
                      <td>{implant.sizes || '—'}</td>
                      <td>{implant.vendor_name || 'N/A'}</td>
                      <td>{implant.total_quantity}</td>
                      <td>{implant.created_by || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
        </>
      )}

      {selectedImplant && (
        <ImplantPopup
          implantName={selectedImplant}
          onClose={handleClosePopup}
        />
      )}
    </Box>
  );
};

export default Implants;
